import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  VpnKey as KeyIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchImages } from "@/store/imagesSlice";
import { useLocalKeys } from "@hooks/useLocalKeys";
import { useNotification } from "@context/NotificationContext";

export default function KeyVault() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { items, loading } = useAppSelector((s) => s.images);
  const { hasKey, keyCount, getAllKeyIds, exportBundle, importBundle } =
    useLocalKeys();

  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchImages());
  }, [dispatch]);

  const localKeyIds = getAllKeyIds();

  // Merge backend images with local keys
  const rows = [
    ...items.map((img) => ({
      id: img.id,
      name: img.name,
      hasLocalKey: hasKey(img.id),
      source: "backend" as const,
    })),
    ...localKeyIds
      .filter((kid) => !items.find((img) => img.id === kid))
      .map((kid) => ({
        id: kid,
        name: null as string | null,
        hasLocalKey: true,
        source: "local-only" as const,
      })),
  ].filter(
    (r) =>
      !search ||
      r.id.toLowerCase().includes(search.toLowerCase()) ||
      r.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const handleExport = useCallback(async () => {
    try {
      const json = await exportBundle();
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `securevault-keys-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      notify(t("keyVault.exported", "Key bundle exported"), "success");
    } catch {
      notify(t("keyVault.exportError", "Export failed"), "error");
    }
  }, [exportBundle, notify, t]);

  const handleImport = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        await importBundle(text);
        notify(t("keyVault.imported", "Keys imported successfully"), "success");
      } catch {
        notify(
          t("keyVault.importError", "Import failed. Invalid file."),
          "error",
        );
      }
    };
    input.click();
  }, [importBundle, notify, t]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
            }}
          >
            {t("keyVault.title", "Key Vault")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <KeyIcon fontSize="small" color="primary" />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {t("keyVault.keyCount", "{{count}} keys stored locally", {
                count: keyCount,
              })}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={keyCount === 0}
          >
            {t("keyVault.export", "Export Bundle")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={handleImport}
          >
            {t("keyVault.import", "Import Bundle")}
          </Button>
        </Box>
      </Box>
      <TextField
        size="small"
        placeholder={t("keyVault.searchPlaceholder", "Search keys...")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 2, width: { xs: "100%", sm: 300 } }}
      />
      {loading ? (
        <Skeleton variant="rectangular" height={300} />
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("keyVault.imageId", "Image ID")}</TableCell>
                  <TableCell>{t("keyVault.imageName", "Name")}</TableCell>
                  <TableCell>{t("keyVault.keyStatus", "Key Status")}</TableCell>
                  <TableCell>{t("keyVault.source", "Source")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          py: 3,
                        }}
                      >
                        {t("keyVault.noKeys", "No keys found")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontFamily: "monospace",
                          }}
                        >
                          {row.id.slice(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{row.name || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            row.hasLocalKey
                              ? t("keyVault.present", "Present")
                              : t("keyVault.missing", "Missing")
                          }
                          color={row.hasLocalKey ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            row.source === "backend"
                              ? t("keyVault.backend", "Server")
                              : t("keyVault.localOnly", "Local Only")
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}
